import { Component, AfterViewInit, OnDestroy, OnChanges, SimpleChanges, Input, Output, EventEmitter, ChangeDetectorRef, NgZone, effect, ViewChild, ElementRef } from '@angular/core';
import * as THREE from 'three';
import * as htmlToImage from 'html-to-image';
import { WorkbenchService } from 'src/app/services/configurators-2d/applications/workbench.service';
import { ShapeTypeEnum } from 'src/app/enum/workbench/shape-type.enum';
import { IWorkbenchPreview } from './i-workbench-preview';
import { ActionButton, DimensionIndicator, MagnetismIcon, ConfigurationPlan3D, ConfigurationEdge3D } from './preview-ui-models';
import { InteractionModeEnum, WorkbenchSelectionStore } from '../../../../stores/workbench/workbench-selection.store';
import { WorkbenchRaycasterService } from './engine/workbench-raycaster.service';
import { WorkbenchSelectionManager } from './logic/workbench-selection-manager.service';
import { ConfigurationEdge } from 'src/app/models/configurators-2d/applications/workbench/configurations/configuration-edge';
import { ConfigurationJamb } from 'src/app/models/configurators-2d/applications/workbench/configurations/configuration-jamb';
import { ConfigurationCutaway } from 'src/app/models/configurators-2d/applications/workbench/configurations/configuration-cutaway';
import { ConfigurationCutting } from 'src/app/models/configurators-2d/applications/workbench/configurations/configuration-cutting';
import { AngleTypeEnum } from 'src/app/enum/workbench/angle-type-enum';
import { ConfigurationPlan } from 'src/app/models/configurators-2d/applications/workbench/configurations/configuration-plan';
import { v4 as uuidv4 } from 'uuid';
import { WorkbenchTextureService } from './services/workbench-texture.service';
import { WorkbenchMeshBuilderService } from './services/workbench-mesh-builder.service';
import { WorkbenchCollisionService } from './services/workbench-collision.service';
import { WorkbenchDimensionsService } from './services/workbench-dimensions.service';
import { WorkbenchSceneService } from './services/workbench-scene.service';
import { WorkbenchPostProcessingService } from './services/workbench-post-processing.service';
import { LineTypeEnum } from 'src/app/enum/workbench/fabric-preview/line-type-enum';
import { ConfigurationSideCut } from 'src/app/models/configurators-2d/applications/workbench/configurations/configuration-side-cut';
import {
  getWorkbenchEdgeDefinition,
  inferWorkbenchEdgeSide,
  normalizePlanEdgeCollections,
  WORKBENCH_EDGE_SIDE_TOLERANCE
} from './services/workbench-edge-utils';
import { getWorkbenchSideSegment, inferWorkbenchSideFromSegment, WorkbenchSide } from './services/workbench-side-geometry';

@Component({
  selector: 'ng-workbench-preview-three',
  templateUrl: './preview-three.component.html',
  styleUrls: ['./preview-three.component.scss']
})
export class WorkbenchPreviewThreeComponent implements AfterViewInit, OnDestroy, OnChanges, IWorkbenchPreview {

  @Input() step: number;
  @Input() subStep: number;
  @Input() isSummary = false;

  @Output() onPlanSelect: EventEmitter<number> = new EventEmitter<number>();
  @Output() onGroupCreate: EventEmitter<number[]> = new EventEmitter<number[]>();
  @Output() onSuperposition: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() onGroupDelete: EventEmitter<number> = new EventEmitter<number>();
  @Output() onSplashbackChange: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('threeContainer', { static: true }) containerRef!: ElementRef<HTMLDivElement>;
  @ViewChild('legendEl') legendEl?: ElementRef<HTMLDivElement>;

  // --- State (Delegated to Service) ---
  private get container(): HTMLElement { return this.sceneService.container; }
  private get renderer(): THREE.WebGLRenderer { return this.sceneService.renderer; }
  private get scene(): THREE.Scene { return this.sceneService.scene; }
  private get camera(): THREE.OrthographicCamera | THREE.PerspectiveCamera { return this.sceneService.camera; }


  // Compatibility Properties (Proxies for Services/Store)
  private get raycaster(): THREE.Raycaster { return this.raycasterService.getRaycaster(); }
  private get pointer(): THREE.Vector2 { return (this.raycasterService as any).pointer; } // Hack: expose pointer from service

  private isDragging = false;
  private isPanning = false;
  private dragPlane: THREE.Plane;
  private dragOffset: THREE.Vector3;
  private resizeObserver: ResizeObserver | null = null;

  // Cutting selection & drag state
  private selectedCuttingData: { planGroupId: number; cuttingIdx: number } | null = null;
  private isDraggingCutting = false;
  private cuttingDragPlane: THREE.Plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
  private draggedCuttingData: {
    plan: ConfigurationPlan;
    cc: ConfigurationCutting;
    outline: THREE.LineSegments;
    planGroup: THREE.Group;
    cuttingIdx: number;
    offsetX: number;
    offsetY: number;
  } | null = null;

  // Splashback cutting drag state
  private isDraggingCuttingOnSplashback = false;
  private draggedSbCuttingData: {
    sb: any;
    cc: any;
    outline: THREE.LineSegments;
    sbGroup: THREE.Group;
    cuttingIdx: number;
    offsetX: number;
    offsetY: number;
  } | null = null;

  // Logical Selection (Managed by Store in the background, synced via effect)
  private selectedObject: THREE.Object3D | null = null;

  // Camera Pan State
  private readonly lastPanPointer: THREE.Vector2 = new THREE.Vector2();

  // Data State
  public actionButtons: ActionButton[] = [];
  public dimensions: DimensionIndicator[] = [];
  public magnetismIcons: MagnetismIcon[] = [];
  public planIndicators: { x: number, y: number, label: string }[] = [];
  public groupIndicators: { x: number, y: number, label: string }[] = [];
  public splashbackIndicators: { x: number, y: number, label: string }[] = [];

  public get is3DMode(): boolean { return this.sceneService.is3DMode; }
  public set is3DMode(value: boolean) { this.sceneService.is3DMode = value; }

  public get showLegend(): boolean {
    return this.step >= 3 
      && !this.is3DMode 
      && (this.legendHasEdgeBand || this.legendHasSideCutBand || this.legendHasJamb
          || this.legendHasStraightCutaway || this.legendHasRoundedCutaway
          || this.legendHasPlanCutting || this.legendHasSplashbackCutting
          || this.legendHasSplashback);
  }

  /** Legend computed items — based on current configuration */
  private get _plans(): ConfigurationPlan[] {
    return this.workbenchService.configuration?.workbenchConfigurationPlans ?? [];
  }

  public get legendHasEdgeBand(): boolean {
    return this._plans.some(p => !p?.materialThickness?.canHaveSideCuts && (p as any).configurationEdges?.length > 0);
  }

  public get legendHasSideCutBand(): boolean {
    return this._plans.some(p => !!p?.materialThickness?.canHaveSideCuts && (p as any).configurationSideCuts?.length > 0);
  }

  public get legendHasJamb(): boolean {
    return this._plans.some(p => (p as any).configurationJambs?.length > 0);
  }

  public get legendHasStraightCutaway(): boolean {
    return this._plans.some(p =>
      (p as any).configurationCutaways?.some((c: any) => c.angleType === AngleTypeEnum.straight)
    );
  }

  public get legendHasRoundedCutaway(): boolean {
    return this._plans.some(p =>
      (p as any).configurationCutaways?.some((c: any) => c.angleType === AngleTypeEnum.rounded)
    );
  }

  public get legendHasPlanCutting(): boolean {
    return this._plans.some(p => (p as any).configurationCuttings?.length > 0);
  }

  public get legendHasSplashbackCutting(): boolean {
    return this._plans.some(p =>
      (p as any).configurationSplashbacks?.some((sb: any) => sb.configurationCuttings?.length > 0)
    );
  }

  public get legendHasSplashback(): boolean {
    return this._plans.some(p => (p as any).configurationSplashbacks?.length > 0);
  }

private overlayUpdatePending = false;
  private isCapturingSnapshot = false;

  // Texture Cache (Removed - Handled by Service)

  // Edit State
  public editingDimension: DimensionIndicator | null = null;
  public editValue: number = 0;

  public isSnapshotting = false;

  // Magnetism State
  private dragMagnetismIcons: MagnetismIcon[] = [];

  constructor(
    private readonly workbenchService: WorkbenchService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly ngZone: NgZone,
    private readonly selectionStore: WorkbenchSelectionStore,
    private readonly raycasterService: WorkbenchRaycasterService,
    private readonly selectionManager: WorkbenchSelectionManager,
    private readonly textureService: WorkbenchTextureService,
    private readonly meshBuilder: WorkbenchMeshBuilderService,
    private readonly collisionService: WorkbenchCollisionService,
    private readonly dimensionsService: WorkbenchDimensionsService,
    private readonly postProcessingService: WorkbenchPostProcessingService,
    public sceneService: WorkbenchSceneService
  ) {
    // Effect to sync store selection with legacy property for compatibility
    effect(() => {
      const planId = this.selectionStore.selectedPlanId();
      if (planId !== null) {
        const obj = this.scene?.children.find(c => c.userData['id'] === planId);
        if (obj) {
          this.selectedObject = obj as THREE.Group;
          this.highlightSelection(obj);
          this.updateUIOverlays();
        } else {
          // Do not clear selection if object not found immediately?
          // If we clear, we lose "memory" if scene is rebuilding.
        }
      } else {
        this.selectedObject = null;
        this.highlightSelection(null);
        this.updateUIOverlays();
      }
    });

    // Effect to sync store mode
    effect(() => {
      this.selectionStore.activeMode();
      if (!this.isCapturingSnapshot) {
        this.updateUIOverlays();
      }
    });
  }

  ngAfterViewInit(): void {
    console.log('Three.js Preview - ngAfterViewInit');
    this.initThreeJS();
    this.startAnimationLoop();

    // Initial render if configuration exists
    if (this.workbenchService.configuration?.workbenchConfigurationPlans) {
      console.log('Loading initial configuration with', this.workbenchService.configuration.workbenchConfigurationPlans.length, 'plans');
      setTimeout(() => {
        this.updatePreview();
      }, 0);
    }
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    this.sceneService.destroy();
    window.removeEventListener('pointermove', this.boundPointerMove);
    window.removeEventListener('pointerup', this.boundPointerUp);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['step'] || changes['isSummary']) {
      // On summary/recap step, use SNAPSHOT mode to display all active option icons
      this.selectionStore.setMode(this.isSummary ? InteractionModeEnum.SNAPSHOT : InteractionModeEnum.NONE);
      if (this.scene) {
        this.updatePreview(); // Rebuild scene to apply step-based visibility (e.g. splashbacks)
        this.updateUIOverlays();
      }
    }
  }

  private initThreeJS(): void {
    const container = this.containerRef.nativeElement;
    if (!container) {
      console.error('Container not found!');
      return;
    }

    this.sceneService.init(container);
    this.raycasterService.initialize(this.container, this.camera, this.scene);

    // Init Post-Processing
    const width = Math.max(1, this.container.clientWidth);
    const height = Math.max(1, this.container.clientHeight);
    this.postProcessingService.init(this.renderer, this.scene, this.camera, width, height);

    // Resize Observer for Composer
    this.resizeObserver = new ResizeObserver(() => {
      const w = this.container.clientWidth;
      const h = this.container.clientHeight;

      if (w <= 0 || h <= 0) return;

      this.sceneService.resize(w, h);
      this.postProcessingService.resize(w, h);
    });

    this.resizeObserver.observe(this.container);

    this.dragPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
    this.dragOffset = new THREE.Vector3();

    // Events
    this.ngZone.runOutsideAngular(() => {
      this.renderer.domElement.addEventListener('pointerdown', this.boundPointerDown);
      window.addEventListener('pointermove', this.boundPointerMove);
      window.addEventListener('pointerup', this.boundPointerUp);
    });
  }

  // Pre-bind events for add/remove
  private readonly boundPointerDown = this.onPointerDown.bind(this);
  private readonly boundPointerMove = this.onPointerMove.bind(this);
  private readonly boundPointerUp = this.onPointerUp.bind(this);

  private startAnimationLoop() {
    this.sceneService.startAnimationLoop(() => {
      if (!this.container || this.container.clientWidth <= 0 || this.container.clientHeight <= 0) {
        return true;
      }

      if (!this.isCapturingSnapshot) {
        this.checkPlanChanges();
        this.updateUIOverlays();
      }

      this.postProcessingService.render();
      return true;
    });
  }

  private readonly planCache = new Map<number, string>();
  private lastConfigurationId: string | null = null;

  private resetForNewConfiguration() {
    this.sceneService.destroy();
    this.planCache.clear();
    this.textureService.clearCache();
    this.selectionStore.reset();
    this.postProcessingService.reset();

    this.initThreeJS();
  }

  private checkPlanChanges() {
    if (!this.workbenchService.configuration?.workbenchConfigurationPlans) return;

    // Detect configuration change (e.g. after duplication) and full reset
    const currentId = this.workbenchService.configuration.id;
    if (currentId && currentId !== this.lastConfigurationId) {
      const isInitialLoad = this.lastConfigurationId === null;
      this.lastConfigurationId = currentId;

      if (!isInitialLoad) {
        this.resetForNewConfiguration();
      } else {
        this.planCache.clear();
        this.textureService.clearCache(); // Clear stale textures from previous session
      }
    }

    let changed = false;
    this.workbenchService.configuration.workbenchConfigurationPlans.forEach(plan => {
      // Create a signature based on dimensions that affect geometry
      const key = `${plan.width}_${plan.length}_${plan.diameter}_${plan.shapeId}_${plan.displayOrder}`;
      const cachedKey = this.planCache.get(plan.displayOrder);

      if (cachedKey !== key) {
        this.planCache.set(plan.displayOrder, key);
        changed = true;
      }
    });

    if (changed) {
      // If data changed but scene hasn't updated yet, trigger update
      this.updatePreview();
    }
  }

  // --- External API called by Parent ---
  public updatePreview() {
    // Store currently selected ID to restore selection after rebuild
    let selectedId = this.selectionStore.selectedPlanId();

    // Fallback to local object if Store is empty (though Store should be Truth)
    if (!selectedId && this.selectedObject) {
      selectedId = this.selectedObject.userData['id'];
    }

    this.clearScene();
    if (!this.workbenchService.configuration?.workbenchConfigurationPlans) return;

    const processedMeshes: THREE.Object3D[] = [];

    this.workbenchService.configuration.workbenchConfigurationPlans.forEach(plan => {
      normalizePlanEdgeCollections(plan);
      const mesh = this.meshBuilder.createShapeMesh(plan);
      if (mesh) {
        mesh.userData['id'] = plan.displayOrder;
        mesh.userData['plan'] = plan;
        if (plan.isInGroup && plan.groupReferenceId) {
          mesh.userData['groupId'] = plan.groupReferenceId;
        }

        // Smart Auto-Placement Logic
        if (this.isPlanAtOrigin(plan)) {
          mesh.updateMatrixWorld(true);

          if (this.collisionService.checkCollisionWithList(mesh, processedMeshes)) {
            const newPos = this.collisionService.findSmartPosition(mesh, processedMeshes);

            mesh.position.copy(newPos);
            mesh.updateMatrixWorld(true);

            const centerOffset = mesh.userData['centerOffset'];
            if (centerOffset) {
              plan.origin.x = newPos.x - centerOffset.x;
              plan.origin.y = newPos.y - centerOffset.y;
            } else {
              plan.origin.x = newPos.x;
              plan.origin.y = newPos.y;
            }
            console.log(`Auto-placed plan ${plan.id} at`, plan.origin);
          }
        }

        this.scene.add(mesh);
        processedMeshes.push(mesh);

        // Re-apply texture from cache or load if path exists
        // Standardized: use plan.displayOrder as the texture cache key throughout
        const textureId = plan.displayOrder;
        const cached = this.textureService.getTexture(textureId);
        if (cached) {
          this.textureService.applyTextureToMesh(mesh, cached);
        } else if ((plan as ConfigurationPlan3D).texturePath) {
          this.textureService.loadImage(
            (plan as ConfigurationPlan3D).texturePath, textureId, this.scene, this.workbenchService.apiUrl,
            (texture) => {
              const planGroup = this.scene.children.find(c => c.userData['id'] === textureId) as THREE.Group;
              if (!planGroup) return;
              // Remove grey sbGroups created without texture, recreate with texture now in cache
              const stale = planGroup.children.filter(c => c.userData?.['type'] === 'splashback');
              stale.forEach(c => planGroup.remove(c));
              const p = planGroup.userData['plan'];
              const t = p?.materialThickness?.thickness || 38;
              this.meshBuilder.createSplashbacks(p, planGroup, t, texture, this.is3DMode);
              this._addSplashbackCuttingOverlays();
            }
          );
        }
      }
    });

    // Restore selection if applicable
    if (selectedId) {
      const newSelectedMesh = this.scene.children.find(c => c.userData['id'] === selectedId);
      if (newSelectedMesh) {
        this.selectedObject = newSelectedMesh as THREE.Group;
        this.highlightSelection(this.selectedObject);
      } else {
        // Only clear if we explicitly failed to find the object for the ID we THOUGHT was selected
        this.selectedObject = null;
        this.highlightSelection(null);
      }
    } else {
      this.highlightSelection(null);
    }

    this.validateAndCreateSplashbacks();
    // Apply cached textures to splashbacks (sbGroups created after the texture cache check above)
    this.scene.children.forEach(obj => {
      if (!obj.userData['plan']) return;
      const tex = this.textureService.getTexture(obj.userData['id']);
      if (tex) this._applySplashbackTexture(obj as THREE.Group, tex);
    });
    this._addCuttingOverlays();
    this._addSplashbackCuttingOverlays();
    this.zoomToFit();
    this.updateUIOverlays();
    this.validateAllPlans();
    this.refreshGroupAssembly();
    this.update2DIndicatorsVisibility();
  }

  private validateAndCreateSplashbacks() {
    if (this.step < 3) return; // Crédences only from step 3 (Quartz crédence step)
    const activePlans = this.scene.children.filter(obj => obj.userData['plan'] && obj.visible);

    activePlans.forEach(group => {
      const plan = group.userData['plan'];
      // Force update matrix world for accurate collision detection
      (group as THREE.Group).updateMatrixWorld(true);

      if (!plan?.configurationSplashbacks || plan.configurationSplashbacks.length === 0) return;

      // Filter invalid splashbacks (those on assembled edges)
      const validSplashbacks: any[] = [];
      const toRemove: any[] = [];

      const centerOffset = group.userData['centerOffset'] || { x: 0, y: 0 };

      // Helper: Local (original plan coords) -> World
      const toWorld = (p: { x: number, y: number }) => {
        // 1. To Local (Centered)
        const v = new THREE.Vector3(p.x - centerOffset.x, p.y - centerOffset.y, 0);
        // 2. To World
        v.applyEuler(group.rotation).add(group.position);
        return v;
      };

      plan.configurationSplashbacks.forEach((sb: any) => {
        if (!sb.startPoint || !sb.endPoint) return;
        // Calculate Midpoint in World Space
        const p1 = toWorld(sb.startPoint);
        const p2 = toWorld(sb.endPoint);
        const mid = p1.clone().add(p2).multiplyScalar(0.5);

        // Check Collision with optimized tolerance
        // We assume isEdgeTouchingOtherPlans handles tolerance correctly
        if (this.collisionService.isEdgeTouchingOtherPlans(mid, plan, this.scene.children)) {
          console.log(`Splashback removed due to assembly: Plan ${plan.id}, SB on edge`);
          toRemove.push(sb);
        } else {
          validSplashbacks.push(sb);
        }
      });

      // Update Plan Data
      if (toRemove.length > 0) {
        plan.configurationSplashbacks = validSplashbacks;
      }

      if (validSplashbacks.length > 0) {
        // Get thickness and texture
        const thickness = plan.materialThickness.thickness;
        // Texture cache key is plan.displayOrder (same as in updatePreview)
        const texture = this.textureService.getTexture(plan.displayOrder);

        // Call Create via Service
        this.meshBuilder.createSplashbacks(plan, group as THREE.Group, thickness, texture || null, this.is3DMode);
      }
    });
  }

  private validateAllPlans() {
    const plans = this.scene.children.filter(c => c.userData['plan']);
    let hasCollision = false;

    plans.forEach(planObj => {
      const isInvalid = this.collisionService.checkInvalidCollision(planObj, this.scene.children);
      if (isInvalid) hasCollision = true;
      this.updateShapeAppearance(planObj, isInvalid);
    });

    if (this.workbenchService.configuration) {
      this.workbenchService.configuration.hasSuperposition = hasCollision;
    }
    this.onSuperposition.emit(hasCollision);
  }

  private zoomToFit() {
    const plans = this.scene.children.filter(obj => obj.userData['plan']);
    const legendNative = this.legendEl?.nativeElement;
    // Pass the bottom edge of the legend as the vertical safe area boundary so the
    // diagram is always positioned below the legend. No horizontal shift needed.
    const safeArea = legendNative
      ? { top: legendNative.offsetTop + legendNative.offsetHeight }
      : undefined;
    this.sceneService.zoomToFit(plans, safeArea);
    this.updateUIOverlays();
  }

  // Alias for Fabric compatibility
  public update(): void {
    this.updatePreview();
  }

  // --- Finishes Implementation ---

  showEdgeControls(show: boolean): void {
    this.selectionStore.setMode(show ? InteractionModeEnum.EDGE : InteractionModeEnum.NONE);
  }

  showJambControls(show: boolean): void {
    this.selectionStore.setMode(show ? InteractionModeEnum.JAMB : InteractionModeEnum.NONE);
  }

  showCutawayControls(show: boolean): void {
    this.selectionStore.setMode(show ? InteractionModeEnum.CUTAWAY : InteractionModeEnum.NONE);
  }

  showCuttingControls(show: boolean): void {
    this.selectionStore.setMode(show ? InteractionModeEnum.CUTTING : InteractionModeEnum.NONE);
  }

  showEdgeCutawayAndJambsSelected(): void {
    this.updateUIOverlays();
  }

  async recreateEdgesCutawaysAndJambs(): Promise<void> {
    this.updatePreview();
  }

  loadPlanCuttings(): void {
    // Rebuild the scene so holes are baked into the mesh geometry, then add overlays.
    this.updatePreview();
  }

  private _addCuttingOverlays(): void {
    const CUTTING_COLOR = 0xdd4400;

    this.scene.children.forEach((obj: THREE.Object3D) => {
      if (!obj.userData['plan']) return;
      const plan = obj.userData['plan'] as ConfigurationPlan;

      // Remove previous cutting indicators
      const toRemove = obj.children.filter(c => c.userData['isCutting'] || c.userData['isCuttingHitArea'] || c.userData['isCuttingHatch']);
      toRemove.forEach(c => {
        (c as any).geometry?.dispose();
        (c as any).material?.dispose();
        obj.remove(c);
      });

      if (!plan.configurationCuttings?.length) return;

      // Z position: top face = thickness/2, +2mm offset (same as edge/jamb indicators)
      const thickness = plan.materialThickness?.thickness || 38;
      const cuttingZ = thickness / 2 + 2;
      const halfL = plan.length / 2;
      const halfW = plan.width / 2;

      plan.configurationCuttings.forEach((cc, index) => {
        if (!cc.cutting) return;

        const cw = cc.cutting.defaultWidth || 300;
        const ch = cc.cutting.defaultLength || 500;
        const ox = cc.origin?.x ?? plan.length / 2;
        const oy = cc.origin?.y ?? plan.width / 2;
        // Convert plan-local (bottom-left) → Three.js group-local (center origin)
        const tx = ox - halfL;
        const ty = oy - halfW;
        const rotAngle = -((cc.rotationState || 0) * Math.PI / 2);

        // Diagonal hatch fill (2D mode only, same style as jamb/cutaway indicators)
        const hatchMat = new THREE.ShaderMaterial({
          transparent: true,
          side: THREE.DoubleSide,
          depthWrite: false,
          uniforms: {
            uColor: { value: new THREE.Color(CUTTING_COLOR) },
            uSpacing: { value: 25.0 },
            uLineWidth: { value: 0.4 }
          },
          vertexShader: `
            varying vec2 vWorldPos;
            void main() {
              vec4 worldPosition = modelMatrix * vec4(position, 1.0);
              vWorldPos = worldPosition.xy;
              gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }`,
          fragmentShader: `
            uniform vec3 uColor;
            uniform float uSpacing;
            uniform float uLineWidth;
            varying vec2 vWorldPos;
            void main() {
              float diag = vWorldPos.x + vWorldPos.y;
              float pattern = mod(diag, uSpacing);
              float line = step(pattern, uSpacing * uLineWidth);
              float alpha = mix(0.15, 0.85, line);
              gl_FragColor = vec4(uColor, alpha);
            }`
        });
        const hatchGeo = new THREE.PlaneGeometry(cw, ch);
        const hatchMesh = new THREE.Mesh(hatchGeo, hatchMat);
        hatchMesh.position.set(tx, ty, cuttingZ - 0.1);
        hatchMesh.rotation.z = rotAngle;
        hatchMesh.userData['isCuttingHatch'] = true;
        hatchMesh.userData['cuttingIndex'] = index;
        hatchMesh.userData['type'] = 'cutting-indicator';
        hatchMesh.visible = !this.is3DMode;
        obj.add(hatchMesh);

        // Wireframe outline (visual indicator)
        const geo = new THREE.PlaneGeometry(cw, ch);
        const edges = new THREE.EdgesGeometry(geo);
        geo.dispose();
        const outline = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: CUTTING_COLOR }));
        outline.position.set(tx, ty, cuttingZ);
        outline.rotation.z = rotAngle;
        outline.userData['isCutting'] = true;
        outline.userData['cuttingIndex'] = index;
        obj.add(outline);

        // Transparent hit area for raycasting (makes dragging easy to initiate)
        const hitGeo = new THREE.PlaneGeometry(cw, ch);
        const hitMat = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, side: THREE.DoubleSide, depthWrite: false });
        const hitMesh = new THREE.Mesh(hitGeo, hitMat);
        hitMesh.position.set(tx, ty, cuttingZ + 0.1);
        hitMesh.rotation.z = rotAngle;
        hitMesh.userData['isCuttingHitArea'] = true;
        hitMesh.userData['cuttingIndex'] = index;
        hitMesh.userData['cuttingOutline'] = outline;
        hitMesh.userData['cuttingHatch'] = hatchMesh;
        obj.add(hitMesh);
      });
    });
  }

  private _applySplashbackTexture(planGroup: THREE.Group, texture: THREE.Texture): void {
    planGroup.children
      .filter(c => c.userData?.['type'] === 'splashback')
      .forEach(sbGroup => {
        const sbMesh = (sbGroup as THREE.Group).children.find(c =>
          c.type === 'Mesh' && !c.userData?.['isSbCuttingHatch'] && !c.userData?.['isSbCuttingHitArea']
        ) as THREE.Mesh | undefined;
        if (!sbMesh) return;
        const edgeLength = sbGroup.userData['edgeLength'] as number || 1000;
        const height = sbGroup.userData['height'] as number || 350;
        const sbTex = texture.clone();
        sbTex.needsUpdate = true;
        sbTex.wrapS = THREE.RepeatWrapping;
        sbTex.wrapT = THREE.RepeatWrapping;
        // ShapeGeometry UVs are world coords in mm: [-edgeLength/2, edgeLength/2]
        // repeat=1/size maps to [-0.5, 0.5], offset=0.5 shifts to [0, 1]
        sbTex.repeat.set(1 / edgeLength, 1 / height);
        sbTex.offset.set(0.5, 0.5);
        const mat = sbMesh.material as THREE.MeshBasicMaterial;
        mat.map = sbTex;
        mat.color.setHex(0xffffff);
        mat.needsUpdate = true;
      });
  }

  private _addSplashbackCuttingOverlays(): void {
    const CUTTING_COLOR = 0xdd4400;

    this.scene.children.forEach((obj: THREE.Object3D) => {
      if (!obj.userData['plan']) return;

      obj.children.forEach((child: THREE.Object3D) => {
        if (child.userData['type'] !== 'splashback') return;
        const sbGroup = child as THREE.Group;

        // Remove previous overlays
        const toRemove = sbGroup.children.filter(c =>
          c.userData['isSbCutting'] || c.userData['isSbCuttingHitArea'] || c.userData['isSbCuttingHatch']
        );
        toRemove.forEach(c => {
          (c as any).geometry?.dispose();
          (c as any).material?.dispose();
          sbGroup.remove(c);
        });

        const sb = sbGroup.userData['splashback'];
        if (!sb?.configurationCuttings?.length) return;

        const edgeLength = sbGroup.userData['edgeLength'] as number;
        const height = sbGroup.userData['height'] as number;
        const Z = 2; // above the splashback mesh (which is at z=0 in sbGroup local space)

        sb.configurationCuttings.forEach((cc: any, index: number) => {
          if (!cc.cutting) return;

          const cw = cc.cutting.defaultWidth || 300;
          const ch = cc.cutting.defaultLength || 500;
          // Convert stored origin (0..edgeLength, 0..height) to sbGroup local (centered)
          const ox = (cc.origin?.x ?? edgeLength / 2) - edgeLength / 2;
          const oy = (cc.origin?.y ?? height / 2) - height / 2;

          // Diagonal hatch fill
          const hatchMat = new THREE.ShaderMaterial({
            transparent: true,
            side: THREE.DoubleSide,
            depthWrite: false,
            uniforms: {
              uColor: { value: new THREE.Color(CUTTING_COLOR) },
              uSpacing: { value: 25.0 },
              uLineWidth: { value: 0.4 }
            },
            vertexShader: `
              varying vec2 vWorldPos;
              void main() {
                vec4 worldPosition = modelMatrix * vec4(position, 1.0);
                vWorldPos = worldPosition.xy;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
              }`,
            fragmentShader: `
              uniform vec3 uColor;
              uniform float uSpacing;
              uniform float uLineWidth;
              varying vec2 vWorldPos;
              void main() {
                float diag = vWorldPos.x + vWorldPos.y;
                float pattern = mod(diag, uSpacing);
                float line = step(pattern, uSpacing * uLineWidth);
                float alpha = mix(0.15, 0.85, line);
                gl_FragColor = vec4(uColor, alpha);
              }`
          });
          const hatchGeo = new THREE.PlaneGeometry(cw, ch);
          const hatchMesh = new THREE.Mesh(hatchGeo, hatchMat);
          hatchMesh.position.set(ox, oy, Z - 0.1);
          hatchMesh.userData['isSbCuttingHatch'] = true;
          hatchMesh.userData['sbCuttingIndex'] = index;
          hatchMesh.visible = !this.is3DMode;
          sbGroup.add(hatchMesh);

          // Outline
          const outlineGeo = new THREE.PlaneGeometry(cw, ch);
          const outlineEdges = new THREE.EdgesGeometry(outlineGeo);
          outlineGeo.dispose();
          const outline = new THREE.LineSegments(outlineEdges, new THREE.LineBasicMaterial({ color: CUTTING_COLOR }));
          outline.position.set(ox, oy, Z);
          outline.userData['isSbCutting'] = true;
          outline.userData['sbCuttingIndex'] = index;
          sbGroup.add(outline);

          // Transparent hit area for raycasting
          const hitGeo = new THREE.PlaneGeometry(cw, ch);
          const hitMat = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, side: THREE.DoubleSide, depthWrite: false });
          const hitMesh = new THREE.Mesh(hitGeo, hitMat);
          hitMesh.position.set(ox, oy, Z + 0.1);
          hitMesh.userData['isSbCuttingHitArea'] = true;
          hitMesh.userData['sbCuttingIndex'] = index;
          hitMesh.userData['sbCuttingOutline'] = outline;
          hitMesh.userData['sbCuttingHatch'] = hatchMesh;
          sbGroup.add(hitMesh);
        });
      });
    });
  }

  async recreateSplashbacks(): Promise<void> {
    this.updatePreview();
  }

  createOrUpdateOptionalPreview(): void {
    this.updatePreview();
  }

  showSplashbackControls(show: boolean): void {
    console.log('showSplashbackControls called with:', show);
    this.selectionStore.setMode(show ? InteractionModeEnum.SPLASHBACK : InteractionModeEnum.NONE);
  }

  actionByConfiguratorStep(): void {
    // On summary step, keep SNAPSHOT mode to show all active option icons
    if (this.isSummary) {
      this.selectionStore.setMode(InteractionModeEnum.SNAPSHOT);
      return;
    }
    // Reset interaction mode when entering any Finitions/Crédence step (step >= 3).
    // For Crédence (step 3 Quartz): mode is immediately overridden to SPLASHBACK
    //   by the explicit showSplashbackControls(true) call in splashbacks.component.
    // For Finitions (step 3 non-Quartz or step 4 Quartz): mode stays NONE until
    //   the user expands a section (edge/jamb/cutaway) which calls showEdgeControls etc.
    if (this.step >= 3) {
      this.selectionStore.setMode(InteractionModeEnum.NONE);
    }
  }

  private toggleEdgeSelection(plan: ConfigurationPlan, localPoint: THREE.Vector3, sideHint?: string) {
    if (!plan || !this.workbenchService.configuration || !sideHint) return;

    const edgeDef = getWorkbenchEdgeDefinition(plan, sideHint);
    if (!edgeDef) return;

    const targetSide = edgeDef.side;
    const segStart = new THREE.Vector3(edgeDef.startPoint.x, edgeDef.startPoint.y, 0);
    const segEnd = new THREE.Vector3(edgeDef.endPoint.x, edgeDef.endPoint.y, 0);

    if (plan.materialThickness?.canHaveSideCuts) {
      // Quartz (canHaveSideCuts): store as ConfigurationSideCut, not ConfigurationEdge
      plan.configurationEdges = [];
      if (!plan.configurationSideCuts) plan.configurationSideCuts = [];

      const TOL = WORKBENCH_EDGE_SIDE_TOLERANCE;
      const sameXY = (a: any, b: any) => a && b && Math.abs(a.x - b.x) < TOL && Math.abs(a.y - b.y) < TOL;
      const existingIdx = plan.configurationSideCuts.findIndex(sc => {
        // Match by transient 'side' (in-session) OR by persistent startPoint/endPoint (after reload)
        if (inferWorkbenchEdgeSide(plan, sc, TOL) === targetSide) return true;
        return (sameXY(sc.startPoint, segStart) && sameXY(sc.endPoint, segEnd)) ||
               (sameXY(sc.startPoint, segEnd) && sameXY(sc.endPoint, segStart));
      });

      if (existingIdx !== -1) {
        plan.configurationSideCuts.splice(existingIdx, 1);
      } else {
        const sc = new ConfigurationSideCut(
          { x: segStart.x, y: segStart.y },
          { x: segEnd.x, y: segEnd.y },
          null, // sideCut reference filled later in summary.component
          edgeDef.length
        );
        (sc as any).side = targetSide;
        plan.configurationSideCuts.push(sc);
      }
    } else {
      plan.configurationSideCuts = [];
      if (!plan.configurationEdges) plan.configurationEdges = [];

      const existingIdx = plan.configurationEdges.findIndex(e => inferWorkbenchEdgeSide(plan, e, WORKBENCH_EDGE_SIDE_TOLERANCE) === targetSide);

      if (existingIdx !== -1) {
        plan.configurationEdges.splice(existingIdx, 1);
      } else {
        const newEdge = new ConfigurationEdge(
          plan.length, plan.width, plan.shape.shapeType,
          { x: segStart.x, y: segStart.y }, { x: segEnd.x, y: segEnd.y },
          plan.colorId || 0, plan.materialId || 0,
          plan.materialThickness ? plan.materialThickness.id : 0,
          edgeDef.lineType, plan.diameter
        ) as ConfigurationEdge3D;

        newEdge.side = targetSide;

        if (plan.materialThickness) {
          newEdge.materialThickness = plan.materialThickness;
        }
        plan.configurationEdges.push(newEdge);
      }
    }

    this.recreateEdgesCutawaysAndJambs();
  }

  private toggleJambSelection(plan: ConfigurationPlan, localPoint: THREE.Vector3, sideHint?: string) {
    if (!plan || !this.workbenchService.configuration || !sideHint) return;

    const side = sideHint;
    const len = plan.length;
    const wid = plan.width;

    let segStart = new THREE.Vector3();
    let segEnd = new THREE.Vector3();

    if (side === 'left') {
      segStart.set(0, wid, 0);
      segEnd.set(0, 0, 0);
    } else if (side === 'right') {
      segStart.set(len, 0, 0);
      segEnd.set(len, wid, 0);
    } else {
      return; // Only left/right supported for now
    }

    if (!plan.configurationJambs) plan.configurationJambs = [];

    const existingIdx = plan.configurationJambs.findIndex((j: any) =>
      j.side === side ||
      (Math.abs(j.startPoint.x - segStart.x) < 2 && Math.abs(j.startPoint.y - segStart.y) < 2)
    );

    if (existingIdx !== -1) {
      plan.configurationJambs.splice(existingIdx, 1);
    } else {
      // Create automatic horizontal edge band for the jamb, only if the material supports edges
      let horizontalEdge: ConfigurationEdge | null = null;
      if (plan.materialThickness?.canHaveEdges) {
        horizontalEdge = new ConfigurationEdge(
          plan.length, plan.width, plan.shape?.shapeType ?? ShapeTypeEnum.Rectangle,
          { x: segStart.x, y: segStart.y }, { x: segEnd.x, y: segEnd.y },
          plan.colorId || 0, plan.materialId || 0,
          plan.materialThickness.id,
          LineTypeEnum.straight, plan.diameter
        );
        horizontalEdge.materialThickness = plan.materialThickness;
      }

      const newJamb = new ConfigurationJamb(
        { x: segStart.x, y: segStart.y },
        { x: segEnd.x, y: segEnd.y },
        [], // vertical edges
        horizontalEdge // horizontal edge band (null si matériau sans chant)
      );
      newJamb.id = uuidv4();
      newJamb.height = 870;
      newJamb.havePolish = false;
      newJamb.have45DegreesSideCut = false;
      (newJamb as any).side = side;

      plan.configurationJambs.push(newJamb);
    }
    this.recreateEdgesCutawaysAndJambs();
  }

  public cutawayMenu: { x: number, y: number, plan: ConfigurationPlan, localPoint: THREE.Vector3, existingIdx: number } | null = null;

  private toggleCutawaySelection(plan: ConfigurationPlan, localPoint: THREE.Vector3) {
    if (!plan || !this.workbenchService.configuration) return;

    const len = plan.length;
    const wid = plan.width;

    const planPoint = localPoint.clone();
    planPoint.x += len / 2;
    // For Angle shapes the mesh bounding box is (0,0)-(len,len) so center Y = len/2
    planPoint.y += (plan.shape.shapeType === ShapeTypeEnum.Angle) ? len / 2 : wid / 2;

    // Corners (in uncentered plan space).
    // For Angle shapes: TL=(0,len), TR=(len,len), BL=(0,0). Exclude diagonal corner (len,0).
    const corners = plan.shape.shapeType === ShapeTypeEnum.Angle
      ? [
          { point: new THREE.Vector3(0, 0, 0) },
          { point: new THREE.Vector3(len, len, 0) },
          { point: new THREE.Vector3(0, len, 0) }
        ]
      : [
          { point: new THREE.Vector3(0, 0, 0) },
          { point: new THREE.Vector3(len, 0, 0) },
          { point: new THREE.Vector3(len, wid, 0) },
          { point: new THREE.Vector3(0, wid, 0) }
        ];

    let bestDist = Infinity;
    let bestCorner = null;
    corners.forEach(c => {
      const dist = planPoint.distanceTo(c.point);
      if (dist < bestDist) {
        bestDist = dist;
        bestCorner = c;
      }
    });

    const CLICK_THRESHOLD = 50;
    if (bestCorner && bestDist < CLICK_THRESHOLD) {
      if (!plan.configurationCutaways) plan.configurationCutaways = [];

      const existingIdx = plan.configurationCutaways.findIndex(c =>
        this.isSamePoint(c.origin, bestCorner.point)
      );

      // Open Menu
      const mesh = this.scene.children.find(c => c.userData['plan'] === plan);
      if (!mesh) return;

      const centeredCorner = bestCorner.point.clone();
      centeredCorner.x -= len / 2;
      // For Angle shapes the bounding box is (0,0)-(len,len) so center Y = len/2
      centeredCorner.y -= (plan.shape.shapeType === ShapeTypeEnum.Angle) ? len / 2 : wid / 2;
      const worldCorner = mesh.localToWorld(centeredCorner.clone());

      if (this.collisionService.isCornerTouchingOtherPlans(worldCorner, plan, this.scene.children)) {
        console.log('Corner is touching another plan - Cutaway disabled');
        return;
      }

      const screenPos = this.toScreenPosition(worldCorner);

      this.cutawayMenu = {
        x: screenPos.x,
        y: screenPos.y,
        plan: plan,
        localPoint: bestCorner.point,
        existingIdx: existingIdx
      };

      this.cdRef.detectChanges();

      setTimeout(() => {
        window.addEventListener('pointerdown', this.boundCloseMenu);
      }, 0);
    }
  }

  // Bound handler for removal
  private readonly boundCloseMenu = (event: any) => {
    this.closeCutawayMenu();
  }

  public applyCutaway(type: 'straight' | 'rounded' | 'remove') {
    console.log('applyCutaway called with:', type);
    if (!this.cutawayMenu) return;

    const { plan, localPoint, existingIdx } = this.cutawayMenu;

    if (type === 'remove') {
      if (existingIdx !== -1) {
        plan.configurationCutaways.splice(existingIdx, 1);
      }
    } else {
      const angleType = type === 'straight' ? AngleTypeEnum.straight : AngleTypeEnum.rounded;

      if (existingIdx !== -1) {
        plan.configurationCutaways[existingIdx].angleType = angleType;
      } else {
        const newCut = new ConfigurationCutaway(
          { x: localPoint.x, y: localPoint.y },
          angleType
        );
        plan.configurationCutaways.push(newCut);
      }
    }

    this.recreateEdgesCutawaysAndJambs();
    this.closeCutawayMenu();
  }

  private toggleSplashback(plan: ConfigurationPlan, edgeId: string) {
    if (!plan.configurationSplashbacks) plan.configurationSplashbacks = [];

    const sideSegment = getWorkbenchSideSegment(plan, edgeId as WorkbenchSide);
    if (!sideSegment) return;

    const p1 = sideSegment.start;
    const p2 = sideSegment.end;

    // Primary: match by edge geometry (robust against float precision and DB round-trips).
    // Fallback: exact coordinate comparison.
    const existingIdx = plan.configurationSplashbacks.findIndex((sb: any) =>
      this.getSplashbackEdgeId(sb, plan) === edgeId ||
      (this.isSamePoint(sb.startPoint, p1) && this.isSamePoint(sb.endPoint, p2)) ||
      (this.isSamePoint(sb.startPoint, p2) && this.isSamePoint(sb.endPoint, p1))
    );

    if (existingIdx !== -1) {
      plan.configurationSplashbacks.splice(existingIdx, 1);
      // Re-normalize displayOrders after removal
      plan.configurationSplashbacks.forEach((sb: any, i: number) => sb.displayOrder = i + 1);
    } else {
      // Determine height: Inherit from existing, or default to 350
      let inheritedHeight = 350;
      for (const p of this.scene.children) {
        if (p.userData['plan']) {
          const configPlan = p.userData['plan'] as ConfigurationPlan;
          const existing = configPlan.configurationSplashbacks?.find((s: any) => s.height > 0);
          if (existing) { inheritedHeight = existing.height; break; }
        }
      }

      // Compute length from start/end points
      const dx = (p2 as any).x - (p1 as any).x;
      const dy = (p2 as any).y - (p1 as any).y;
      const length = Math.round(Math.sqrt(dx * dx + dy * dy));

      // displayOrder = next index across ALL plans' splashbacks
      let maxOrder = 0;
      for (const p of this.scene.children) {
        if (p.userData['plan']) {
          const configPlan = p.userData['plan'] as ConfigurationPlan;
          configPlan.configurationSplashbacks?.forEach((sb: any) => {
            if (sb.displayOrder > maxOrder) maxOrder = sb.displayOrder;
          });
        }
      }

      const newSb: any = {
        id: uuidv4(),
        startPoint: p1,
        endPoint: p2,
        length,
        height: inheritedHeight,
        displayOrder: maxOrder + 1,
        configurationCuttings: [],
        configurationSideCuts: []
      };
      plan.configurationSplashbacks.push(newSb);
    }
    // Do NOT call recreateEdgesCutawaysAndJambs() here — onActionButtonClick already
    // calls updatePreview() after this method returns, so a second rebuild is unnecessary.
  }

  private getSplashbackEdgeId(sb: any, plan: ConfigurationPlan): string | null {
    return inferWorkbenchSideFromSegment(plan, sb?.startPoint, sb?.endPoint, 1);
  }

  public closeCutawayMenu() {
    this.cutawayMenu = null;
    window.removeEventListener('pointerdown', this.boundCloseMenu);
    this.cdRef.detectChanges();
  }

  private pointToSegmentDistance(p: THREE.Vector3, v: THREE.Vector3, w: THREE.Vector3): number {
    const l2 = v.distanceToSquared(w);
    if (l2 == 0) return p.distanceTo(v);
    let t = ((p.x - v.x) * (w.x - v.x) + (p.y - v.y) * (w.y - v.y)) / l2;
    t = Math.max(0, Math.min(1, t));
    const proj = new THREE.Vector3(v.x + t * (w.x - v.x), v.y + t * (w.y - v.y), 0);
    return p.distanceTo(proj);
  }

  private isSamePoint(p1: any, p2: any): boolean {
    if (!p1 || !p2) return false;
    const TOL = 1;
    return Math.abs(p1.x - p2.x) < TOL && Math.abs(p1.y - p2.y) < TOL;
  }

  private clearScene() {
    const objectsToRemove = this.scene.children.filter(obj =>
      obj.type !== 'AxesHelper' &&
      obj.type !== 'GridHelper' &&
      obj.type !== 'AmbientLight' &&
      obj.type !== 'DirectionalLight' &&
      !obj.userData?.['isHelper']
    );

    objectsToRemove.forEach(obj => {
      this.scene.remove(obj);
      this.disposeObject(obj);
    });
  }

  private disposeObject(obj: THREE.Object3D) {
    obj.traverse((child: any) => {
      if (child.geometry) {
        child.geometry.dispose();
      }
      if (child.material) {
        if (Array.isArray(child.material)) {
          child.material.forEach((mat: THREE.Material) => {
            // Do NOT dispose map/texture as it might be shared/cached by TextureService
            // (mat as any).map?.dispose();
            mat.dispose();
          });
        } else {
          child.material.dispose();
        }
      }
    });
  }

  // --- Interaction ---

  private onPointerDown(event: PointerEvent) {
    // If user clicks on the 3D canvas while editing a dimension, close it.
    // (A click inside the input itself is stopped by stopPropagation)
    if (this.editingDimension) {
      const originalValue = Number.parseFloat(this.editingDimension.value);
      if (this.editValue !== originalValue) {
        this.onDimensionSubmit();
      } else {
        this.onDimensionCancel();
      }
    }

    // Cutting drag: only allowed when the cutting section is active (CUTTING mode)
    if (this.selectionStore.activeMode() === InteractionModeEnum.CUTTING && event.button === 0) {
      this.raycasterService.updatePointer(event);
      this.raycasterService.performRaycast();
      const cuttingHit = this.selectionStore.rawIntersections()
        .find(hit => hit.object.userData?.['isCuttingHitArea']);
      if (cuttingHit) {
        const hitMesh = cuttingHit.object;
        const planGroup = hitMesh.parent as THREE.Group;
        const plan = planGroup?.userData['plan'] as ConfigurationPlan;
        const cuttingIdx = hitMesh.userData['cuttingIndex'] as number;
        const cc = plan?.configurationCuttings?.[cuttingIdx];
        const outline = hitMesh.userData['cuttingOutline'] as THREE.LineSegments;
        if (plan && cc && outline) {
          const localHit = planGroup.worldToLocal(cuttingHit.point.clone());
          this.cuttingDragPlane.setFromNormalAndCoplanarPoint(
            new THREE.Vector3(0, 0, 1),
            new THREE.Vector3(0, 0, 0)
          );
          this.isDraggingCutting = true;
          this.selectedCuttingData = { planGroupId: planGroup.userData['id'], cuttingIdx };
          this.draggedCuttingData = {
            plan, cc, outline, planGroup, cuttingIdx,
            offsetX: outline.position.x - localHit.x,
            offsetY: outline.position.y - localHit.y
          };
          return;
        }
      }
      // No cutting hit → deselect
      this.selectedCuttingData = null;
    }

    // Splashback cutting drag: allowed in SPLASHBACK mode
    if (this.selectionStore.activeMode() === InteractionModeEnum.SPLASHBACK && event.button === 0) {
      this.raycasterService.updatePointer(event);
      this.raycasterService.performRaycast();
      const sbCuttingHit = this.selectionStore.rawIntersections()
        .find(hit => hit.object.userData?.['isSbCuttingHitArea']);
      if (sbCuttingHit) {
        const hitMesh = sbCuttingHit.object;
        const sbGroup = hitMesh.parent as THREE.Group;
        const sb = sbGroup?.userData['splashback'];
        const cuttingIdx = hitMesh.userData['sbCuttingIndex'] as number;
        const cc = sb?.configurationCuttings?.[cuttingIdx];
        const outline = hitMesh.userData['sbCuttingOutline'] as THREE.LineSegments;
        if (sb && cc && outline) {
          const localHit = sbGroup.worldToLocal(sbCuttingHit.point.clone());
          this.cuttingDragPlane.setFromNormalAndCoplanarPoint(
            new THREE.Vector3(0, 0, 1),
            sbCuttingHit.point.clone()
          );
          this.isDraggingCuttingOnSplashback = true;
          this.draggedSbCuttingData = {
            sb, cc, outline, sbGroup, cuttingIdx,
            offsetX: outline.position.x - localHit.x,
            offsetY: outline.position.y - localHit.y
          };
          return;
        }
      }
    }

    // Lock interaction in Decors step (Step 2+)
    if (this.step >= 2) return;

    this.raycasterService.updatePointer(event);

    // Check Left Click for Selection/Drag
    if (event.button === 0) { // Left Button
      // Perform Raycast via Service
      this.raycasterService.performRaycast();

      // Delegate Selection Logic via Manager
      this.selectionManager.handleSelection();

      // Legacy Drag Logic Integration
      const validHit = this.selectionStore.rawIntersections().find(hit =>
        hit.object.parent?.type === 'Group' &&
        hit.object.parent?.userData['id']
      );

      if (validHit) {
        const targetGroup = validHit.object.parent as THREE.Group;

        if (this.selectedObject !== targetGroup) {
          // Update Local State IMMEDIATELY for Dragging
          this.selectedObject = targetGroup;
          this.highlightSelection(this.selectedObject);

          // Notify Parent/Store
          this.onPlanSelect.emit(targetGroup.userData['id']);
        }

        this.isDragging = true;

        // Set Drag Plane to Z=0 (where objects are)
        this.dragPlane.setFromNormalAndCoplanarPoint(
          new THREE.Vector3(0, 0, 1),
          new THREE.Vector3(0, 0, 0)
        );

        // Calculate offset
        const intersectPoint = new THREE.Vector3();
        const raycaster = this.raycasterService.getRaycaster();
        if (raycaster.ray.intersectPlane(this.dragPlane, intersectPoint)) {
          // Offset = ObjectPos - ClickPos
          this.dragOffset.subVectors(targetGroup.position, intersectPoint);
        }
      } else {
        // Deselect if no valid object hit
        // Handled by Manager (click on void) -> Effect updates selectedObject
        this.onPlanSelect.emit(null);

        // Allow panning on background
        this.isPanning = true;
        this.lastPanPointer.copy(this.pointer);
      }
    } else if (event.button === 1 || event.button === 2) { // Middle or Right
      this.isPanning = true;
      this.lastPanPointer.copy(this.pointer);
    }
  }

  private onPointerMove(event: PointerEvent) {
    this.raycasterService.updatePointer(event);

    if (this.isDraggingCutting && this.draggedCuttingData) {
      this.raycasterService.performRaycast();
      const raycaster = this.raycasterService.getRaycaster();
      const intersectPoint = new THREE.Vector3();
      if (raycaster.ray.intersectPlane(this.cuttingDragPlane, intersectPoint)) {
        const { plan, cc, outline, planGroup, cuttingIdx, offsetX, offsetY } = this.draggedCuttingData;
        const localPoint = planGroup.worldToLocal(intersectPoint.clone());
        const newLocalX = localPoint.x + offsetX;
        const newLocalY = localPoint.y + offsetY;
        // Clamp within plan bounds
        const halfL = plan.length / 2;
        const halfW = plan.width / 2;
        const baseW = cc.cutting?.defaultWidth || 300;
        const baseH = cc.cutting?.defaultLength || 500;
        const isRotated90 = (cc.rotationState || 0) % 2 === 1;
        const halfCW = (isRotated90 ? baseH : baseW) / 2;
        const halfCH = (isRotated90 ? baseW : baseH) / 2;
        const clampedX = Math.max(-halfL + halfCW, Math.min(halfL - halfCW, newLocalX));
        const clampedY = Math.max(-halfW + halfCH, Math.min(halfW - halfCH, newLocalY));
        // Move outline and hit area
        outline.position.x = clampedX;
        outline.position.y = clampedY;
        const hitArea = planGroup.children.find(
          c => c.userData['isCuttingHitArea'] && c.userData['cuttingIndex'] === cuttingIdx
        );
        if (hitArea) { hitArea.position.x = clampedX; hitArea.position.y = clampedY; }
        const hatch = planGroup.children.find(
          c => c.userData['isCuttingHatch'] && c.userData['cuttingIndex'] === cuttingIdx
        );
        if (hatch) { hatch.position.x = clampedX; hatch.position.y = clampedY; }
      }
      return;
    }

    if (this.isDraggingCuttingOnSplashback && this.draggedSbCuttingData) {
      this.raycasterService.performRaycast();
      const raycaster = this.raycasterService.getRaycaster();
      const intersectPoint = new THREE.Vector3();
      if (raycaster.ray.intersectPlane(this.cuttingDragPlane, intersectPoint)) {
        const { cc, outline, sbGroup, cuttingIdx, offsetX, offsetY } = this.draggedSbCuttingData;
        const localPoint = sbGroup.worldToLocal(intersectPoint.clone());
        const newLocalX = localPoint.x + offsetX;
        const newLocalY = localPoint.y + offsetY;
        const edgeLength = sbGroup.userData['edgeLength'] as number;
        const height = sbGroup.userData['height'] as number;
        const cw = cc.cutting?.defaultWidth || 300;
        const ch = cc.cutting?.defaultLength || 500;
        const clampedX = Math.max(-edgeLength / 2 + cw / 2, Math.min(edgeLength / 2 - cw / 2, newLocalX));
        const clampedY = Math.max(-height / 2 + ch / 2, Math.min(height / 2 - ch / 2, newLocalY));
        outline.position.x = clampedX;
        outline.position.y = clampedY;
        const hitArea = sbGroup.children.find(c => c.userData['isSbCuttingHitArea'] && c.userData['sbCuttingIndex'] === cuttingIdx);
        if (hitArea) { hitArea.position.x = clampedX; hitArea.position.y = clampedY; }
        const hatch = sbGroup.children.find(c => c.userData['isSbCuttingHatch'] && c.userData['sbCuttingIndex'] === cuttingIdx);
        if (hatch) { hatch.position.x = clampedX; hatch.position.y = clampedY; }
      }
      return;
    }

    if (this.isDragging && this.selectedObject) {
      this.raycasterService.performRaycast(); // Update raycaster state
      const raycaster = this.raycasterService.getRaycaster();

      const intersectPoint = new THREE.Vector3();
      if (raycaster.ray.intersectPlane(this.dragPlane, intersectPoint)) {
        let newPos = intersectPoint.add(this.dragOffset);

        // Check for magnetism (snapping to edges)
        const snapResult = this.checkMagnetism(this.selectedObject, newPos);
        if (snapResult.snapped) {
          newPos = snapResult.position;
          // Magnetism icons are now updated in updateUIOverlays
        } else {
          // Do nothing for icons, let updateUIOverlays handle it
        }

        this.selectedObject.position.copy(newPos);
        this.selectedObject.updateMatrixWorld(true);

        // Check for invalid collisions for ALL plans, not just selected
        this.validateAllPlans();

        // Ensure selected appearance is maintained (blue if valid, red if invalid)
        const hasInvalidCollision = this.collisionService.checkInvalidCollision(this.selectedObject, this.scene.children);
        this.updateShapeAppearance(this.selectedObject, hasInvalidCollision);
        this.updateHelpers(); // Ensure helpers follow the object
        this.updateUIOverlays(); // Update Dimensions in real-time
      }
    } else if (this.isPanning) {
      // Panning Logic (Orthographic)
      if (this.camera instanceof THREE.OrthographicCamera) {
        // Convert pointer delta to world units
        const dx = (event.movementX / this.container.clientWidth) * (this.camera.right - this.camera.left) / this.camera.zoom;
        const dy = (event.movementY / this.container.clientHeight) * (this.camera.top - this.camera.bottom) / this.camera.zoom;

        this.camera.position.x -= dx; // Inverted
        this.camera.position.y += dy; // Standard Y up in 3D, but check coord system
        this.updateUIOverlays();
      }
    }
  }

  private updateHelpers() {
    this.scene.children.forEach(c => {
      if (c instanceof THREE.BoxHelper) {
        c.update();
      }
    });
  }

  // --- Collision & Magnetism Logic ---

  private checkMagnetism(obj: THREE.Object3D, proposedPos: THREE.Vector3): { snapped: boolean, position: THREE.Vector3, icons: MagnetismIcon[] } {
    // Calculate visible world height — works for both ortho and perspective cameras
    const visibleHeight = this.sceneService.getVisibleWorldHeight();

    // Delegate to service
    const result = this.collisionService.checkMagnetism(
      obj,
      proposedPos,
      this.scene,
      visibleHeight,
      this.container.clientHeight
    );

    // User requested NO icons for magnetism/snap (only assembly icon logic remains elsewhere)
    this.dragMagnetismIcons = [];

    return { snapped: result.snapped, position: result.position, icons: [] };
  }

  private updateShapeAppearance(obj: THREE.Object3D, isInvalid: boolean) {
    if (!obj) return;

    // Check if it's a group or mesh
    const mesh = obj instanceof THREE.Group ? obj.children.find(c => c.type === 'Mesh') as THREE.Mesh : obj as THREE.Mesh;

    // Check DTO for texture path or Service Cache
    const plan = obj.userData['plan'];
    const hasTexture = plan && (plan.texturePath || this.textureService.getTexturePath(Number(plan.displayOrder)));

    if (mesh?.material) {
      if (Array.isArray(mesh.material)) {
        const mats = mesh.material as THREE.MeshBasicMaterial[];

        // Top (0)
        if (mats[0]) {
          if (isInvalid) {
            mats[0].color.setHex(0xff6666);
          } else if (obj === this.selectedObject && this.step < 2) {
            if (!mats[0].map && !hasTexture) mats[0].color.setHex(0xaaccff);
            else mats[0].color.setHex(0xffffff);
          } else {
            mats[0].color.setHex(0xffffff);
          }
        }

        // Side (1)
        if (mats[1]) {
          if (isInvalid) {
            mats[1].color.setHex(0xff6666);
          } else if (obj === this.selectedObject && this.step < 2) {
            if (!hasTexture) mats[1].color.setHex(0xaaccff); // Sides usually don't have texture map on index 1, but if main has texture, maybe we want side white too? Or keep side blue? User said "see texture".
            // If main face has texture, let's keep side blue for contrast OR white?
            // Usually sides match. Let's make it white if hasTexture to avoid weird mix.
            else mats[1].color.setHex(0xffffff);
          } else {
            mats[1].color.setHex(0xffffff);
          }
        }

      } else {
        const mat = mesh.material as THREE.MeshBasicMaterial;
        if (isInvalid) {
          mat.color.setHex(0xff6666);
        } else if (obj === this.selectedObject && this.step < 2) {
          if (!mat.map && !hasTexture) mat.color.setHex(0xaaccff);
          else mat.color.setHex(0xffffff);
        } else if (mat.map) {
          mat.color.setHex(0xffffff);
        } else {
          mat.color.setHex(0xcccccc);
        }
      }
    }
  }

  private onPointerUp(event: PointerEvent) {
    if (this.isDraggingCutting && this.draggedCuttingData) {
      const { plan, cc, outline } = this.draggedCuttingData;
      // Save new position: convert group-local centered → plan-local bottom-left
      cc.origin = { x: outline.position.x + plan.length / 2, y: outline.position.y + plan.width / 2 };
      this.isDraggingCutting = false;
      this.draggedCuttingData = null;
      // Rebuild mesh with updated hole position, overlays re-added by updatePreview
      this.updatePreview();
      return;
    }

    if (this.isDraggingCuttingOnSplashback && this.draggedSbCuttingData) {
      const { cc, outline, sbGroup } = this.draggedSbCuttingData;
      const edgeLength = sbGroup.userData['edgeLength'] as number;
      const height = sbGroup.userData['height'] as number;
      // Convert sbGroup-local centered → stored (0..edgeLength, 0..height)
      cc.origin = { x: outline.position.x + edgeLength / 2, y: outline.position.y + height / 2 };
      this.isDraggingCuttingOnSplashback = false;
      this.draggedSbCuttingData = null;
      this.updatePreview();
      return;
    }

    const wasActive = this.isDragging || this.isPanning;

    try {
      if (this.isDragging && this.selectedObject) {
        // Save new position to plan
        const plan = this.selectedObject.userData['plan'];
        if (plan) {
          const centerOffset = this.selectedObject.userData['centerOffset'];
          if (centerOffset) {
            plan.origin.x = this.selectedObject.position.x - centerOffset.x;
            plan.origin.y = this.selectedObject.position.y - centerOffset.y;
          } else {
            plan.origin.x = this.selectedObject.position.x;
            plan.origin.y = this.selectedObject.position.y;
          }
          console.log('Plan position updated:', plan.id, plan.origin);
        }
      }

      // Validate collisions and groups after interaction
      this.validateAllPlans();
      this.refreshGroupAssembly();
      this.highlightSelection(this.selectedObject);

    } catch (err) {
      console.error('Error in onPointerUp:', err);
    } finally {
      this.isDragging = false;
      this.isPanning = false;
      this.dragMagnetismIcons = []; // Ensure cleared

      // Only update if we were actually interacting with the canvas
      if (wasActive) {
        this.zoomToFit();
      }
    }
  }

  public rotatePlanLeft(planId: number) {
    this.rotatePlan(planId, 90);
  }

  public rotatePlanRight(planId: number) {
    this.rotatePlan(planId, -90);
  }

  private rotatePlan(planId: number, angle: number) {
    const obj = this.scene.children.find(c => c.userData['id'] === planId);
    if (!obj) return;

    const plan = obj.userData['plan'];
    if (!plan) return;

    // Update plan rotation state (simple increment for now)
    // Assuming 0, 1, 2, 3 for 0, 90, 180, 270
    // If angle is 90, decrement state (CCW). If -90, increment state (CW).
    // Fabric logic: usually 1 = -90deg?
    // Let's just update the visual rotation for now.
    obj.rotation.z += THREE.MathUtils.degToRad(angle);

    // Save rotation to plan so it persists on rebuild (flip/resize)
    plan.rotation = obj.rotation.z;

    // Update plan data if needed (mock)
    // plan.shapeRotationState = ...

    this.updateUIOverlays();
    this.validateAllPlans(); // Check collisions after rotation
  }

  private onWheel(event: WheelEvent) {
    event.preventDefault();
    const zoomSpeed = 0.001;
    if (this.camera instanceof THREE.OrthographicCamera || this.camera instanceof THREE.PerspectiveCamera) {
      const newZoom = this.camera.zoom - event.deltaY * zoomSpeed;
      this.camera.zoom = Math.max(0.1, Math.min(newZoom, 5));
      this.camera.updateProjectionMatrix();
      this.updateUIOverlays();
    }
  }

  private updatePointer(event: PointerEvent) {
    const rect = this.container.getBoundingClientRect();
    this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
  }

  // --- Visuals ---

  private highlightSelection(obj: THREE.Object3D | null) {
    // No outline highlight on Finitions (step 3) and Récapitulatif (step 4)
    if (this.step >= 3) {
      this.postProcessingService.updateSelection([]);
      return;
    }
    this.postProcessingService.updateSelection(obj ? [obj] : []);

    // Bring selected object to front visually (artificial Z-index)
    // We change Z position slightly. Collision logic ignores Z, so this is safe.

    // Reset all styles
    this.scene.children.forEach((child: any) => {
      if (child.type === 'Group') {
        // Reset style and Z-order
        const mesh = child.children[0] as THREE.Mesh;

        const plan = child.userData['plan'];
        const hasTexture: boolean = plan?.texturePath !== undefined; // check DTO or cast

        // Determine base color: Red if invalid, White if normal (User requested White for unselected)
        const isInvalid = this.collisionService.checkInvalidCollision(child, this.scene.children);
        const defaultColor = isInvalid ? 0xff6666 : 0xffffff;

        if (Array.isArray(mesh.material)) {
          // BoxGeometry with multiple materials
          mesh.material.forEach((mat: THREE.MeshBasicMaterial) => {
            if (isInvalid) {
              mat.color.setHex(0xff6666);
            } else {
              if (mat.map || hasTexture) mat.color.setHex(0xffffff);
              else mat.color.setHex(defaultColor);
            }
            if (mat.map) mat.needsUpdate = true;
          });
        } else {
          const mat = mesh.material as THREE.MeshBasicMaterial;
          if (isInvalid) {
            mat.color.setHex(0xff6666);
          } else if (mat.map || hasTexture) {
            mat.color.setHex(0xffffff);
          } else {
            mat.color.setHex(defaultColor);
          }

          if (mat.map) mat.needsUpdate = true;
        }

        child.position.z = 0;
        child.renderOrder = 0;

        // Remove helper box if exists
        const oldBox = child.getObjectByName('selectionBox');
        if (oldBox) child.remove(oldBox);
      }
    });

    if (obj) {
      const groupId = obj.userData['groupId'];

      // Find all objects to highlight
      let targets = [obj];
      if (this.step >= 2 && groupId) {
        targets = this.scene.children.filter(c => c.userData['groupId'] === groupId);
      }
      this.postProcessingService.updateSelection(targets);

      targets.forEach((target: any) => {
        const mesh = target.children[0] as THREE.Mesh;
        const plan = target.userData['plan'];
        const hasTexture = plan && (plan.texturePath || this.textureService.getTexturePath(Number(plan.displayOrder)));
        const isInvalid = this.collisionService.checkInvalidCollision(target, this.scene.children);

        if (Array.isArray(mesh.material)) {
          const mats = mesh.material as THREE.MeshBasicMaterial[];
          const plan = target.userData['plan'];
          const isCylinder = plan?.shape?.shapeType === ShapeTypeEnum.Circle;

          const topMatIndex = isCylinder ? 1 : 0;
          const sideMatIndex = isCylinder ? 0 : 1;

          // Top Face
          if (mats[topMatIndex]) {
            if (isInvalid) {
              mats[topMatIndex].color.setHex(0xff6666);
            } else if (this.step < 2) {
              if (!mats[topMatIndex].map && !hasTexture) mats[topMatIndex].color.setHex(0xaaccff);
              else mats[topMatIndex].color.setHex(0xffffff);
            } else {
              mats[topMatIndex].color.setHex(0xffffff);
            }
          }

          // Side Face
          if (mats[sideMatIndex]) {
            if (isInvalid) {
              mats[sideMatIndex].color.setHex(0xff6666);
            } else if (this.step < 2) {
              mats[sideMatIndex].color.setHex(0xaaccff);
            } else {
              mats[sideMatIndex].color.setHex(0xffffff);
            }
          }
        } else {
          const mat = mesh.material as THREE.MeshBasicMaterial;
          if (isInvalid) {
            mat.color.setHex(0xff6666);
          } else if (this.step < 2) {
            if (!mat.map && !hasTexture) mat.color.setHex(0xaaccff);
            else mat.color.setHex(0xffffff);
          } else {
            mat.color.setHex(0xffffff);
          }
        }
        // Bring to top
        target.position.z = 10;
        target.renderOrder = 999;
      });
    }
  }

  public loadImage(imagePath: string, planId: number) {
    if (!this.scene) return;
    this.textureService.loadImage(imagePath, planId, this.scene, this.workbenchService.apiUrl);
  }

  private updateUIOverlays() {
    // Throttle: If already requested, skip
    if (this.overlayUpdatePending) return;

    this.overlayUpdatePending = true;
    requestAnimationFrame(() => {
      this.overlayUpdatePending = false;
      this._updateUIOverlays();
    });
  }

  private _updateUIOverlays() {
    // Recalculate positions of HTML overlays (Buttons, Dimensions)
    // Project 3D positions to Screen 2D

    // Delegate to Service
    const activePlans = this.scene.children.filter(obj => obj.userData['plan'] && obj.visible);

    const result = this.dimensionsService.calculateOverlays(
      activePlans,
      this.camera,
      this.container.clientWidth, // Use actual current width/height
      this.container.clientHeight,
      this.step,
      this.selectionStore.activeMode(),
      this.selectedObject,
      this.is3DMode,
      this.selectedCuttingData
    );

    this.actionButtons = result.actionButtons;
    this.dimensions = result.dimensions;

    this.planIndicators = result.planIndicators;
    this.groupIndicators = result.groupIndicators;

    // Merge drag icons with static icons (prioritize drag icons)
    this.magnetismIcons = [...result.magnetismIcons, ...this.dragMagnetismIcons];

    this.splashbackIndicators = result.splashbackIndicators;

    // Recalculate cutaway menu screen position if open (camera may have changed via zoomToFit)
    if (this.cutawayMenu) {
      const mesh = this.scene.children.find(c => c.userData['plan'] === this.cutawayMenu!.plan);
      if (mesh) {
        // localPoint is stored in uncentered plan space — center it before localToWorld
        const plan = this.cutawayMenu.plan;
        const centeredPoint = this.cutawayMenu.localPoint.clone();
        centeredPoint.x -= plan.length / 2;
        centeredPoint.y -= (plan.shape.shapeType === ShapeTypeEnum.Angle) ? plan.length / 2 : plan.width / 2;
        const worldPoint = mesh.localToWorld(centeredPoint);
        const screenPos = this.toScreenPosition(worldPoint);
        this.cutawayMenu = { ...this.cutawayMenu, x: screenPos.x, y: screenPos.y };
      }
    }

    this.cdRef.detectChanges();
  }

  public onDimensionClick(dim: DimensionIndicator) {
    if (dim.isReadOnly) return; // Prevent editing for read-only dimensions

    console.log('[PreviewThree] onDimensionClick', dim, 'step:', this.step);
    if (!dim.planId || !dim.dimensionType) {
      console.warn('Click ignored:', { step: this.step, planId: dim.planId, type: dim.dimensionType });
      return;
    }
    this.editingDimension = dim;
    this.editValue = Number.parseFloat(dim.value);
    this.cdRef.detectChanges();
  }

  public onDimensionSubmit() {
    if (!this.editingDimension?.planId) return;

    const plan = this.workbenchService.configuration.workbenchConfigurationPlans.find(p => p.displayOrder === this.editingDimension.planId);

    if (plan) {
      // Capture old dimensions before update (needed for cutaway origin sync)
      const oldLength = plan.length;
      const oldWidth = plan.width;

      // Update Plan Data
      if (this.editingDimension.dimensionType === 'width') plan.width = this.editValue;
      else if (this.editingDimension.dimensionType === 'length') plan.length = this.editValue;
      else if (this.editingDimension.dimensionType === 'diameter') plan.diameter = this.editValue;

      // Sync jamb start/end points to new plan dimensions
      this._syncJambPointsToPlanDimensions(plan);

      // Sync cutaway origins to new plan dimensions
      this._syncCutawayOriginsToPlanDimensions(plan, oldLength, oldWidth);

      // Trigger Full Update (Redraw Mesh)
      this.updatePreview();
    }

    this.editingDimension = null;
    this.cdRef.detectChanges();
  }

  public onDimensionCancel() {
    this.editingDimension = null;
    this.cdRef.detectChanges();
  }

  /**
   * Met à jour les startPoint/endPoint de chaque jambage après redimensionnement du plan.
   * Le side est détecté via la propriété transiente `side` ou via les coordonnées existantes.
   */
  private _syncJambPointsToPlanDimensions(plan: ConfigurationPlan): void {
    if (!plan.configurationJambs?.length) return;
    const len = plan.length;
    const wid = plan.width;

    plan.configurationJambs.forEach((jamb: any) => {
      // Détecter le side : propriété transiente ou déduction géométrique (x ≈ 0 → left, x > 0 → right)
      const side = jamb.side ?? (jamb.startPoint?.x < 1 && jamb.endPoint?.x < 1 ? 'left' : 'right');

      if (side === 'left') {
        jamb.startPoint = { x: 0, y: wid };
        jamb.endPoint = { x: 0, y: 0 };
      } else if (side === 'right') {
        jamb.startPoint = { x: len, y: 0 };
        jamb.endPoint = { x: len, y: wid };
      }

      // Mettre à jour le chant horizontal du jambage si présent
      if (jamb.horizontalWorkbenchConfigurationEdge) {
        jamb.horizontalWorkbenchConfigurationEdge.startPoint = { ...jamb.startPoint };
        jamb.horizontalWorkbenchConfigurationEdge.endPoint = { ...jamb.endPoint };
      }
    });
  }

  /**
   * Met à jour les origines de chaque découpe d'angle après redimensionnement du plan.
   * Les origines sont stockées en coordonnées locales (0..length, 0..width) ; si une
   * dimension change, les coins qui dépendent de cette valeur doivent être mis à jour.
   * Pour la forme Angle, les coins TR et TL utilisent `length` sur les deux axes.
   */
  private _syncCutawayOriginsToPlanDimensions(plan: ConfigurationPlan, oldLength: number, oldWidth: number): void {
    if (!plan.configurationCutaways?.length) return;
    const newL = plan.length;
    const newW = plan.width;
    const TOL = 2;
    const isAngle = plan.shape?.shapeType === ShapeTypeEnum.Angle;

    plan.configurationCutaways.forEach((cutaway: any) => {
      const origin = cutaway.origin;
      if (!origin) return;
      // X coordinate: matches old length → update to new length
      if (Math.abs(origin.x - oldLength) < TOL) origin.x = newL;
      // Y coordinate: Angle corners use length on Y axis; other shapes use width
      const oldYMax = isAngle ? oldLength : oldWidth;
      const newYMax = isAngle ? newL : newW;
      if (Math.abs(origin.y - oldYMax) < TOL) origin.y = newYMax;
    });
  }

  public trackById(index: number, item: any): any {
    return item.id;
  }

  public onActionButtonClick(btn: ActionButton) {
    if (this.isSnapshotting || this.isSummary) return;

    if (btn.type === 'finish_click' && btn.data) {
      const plan = this.workbenchService.configuration.workbenchConfigurationPlans.find(p => p.displayOrder === btn.targetId);
      if (plan && btn.data.mode) {
        const localPoint = btn.data.localPoint as THREE.Vector3;

        if (btn.data.mode === 'edge') {
          this.toggleEdgeSelection(plan, localPoint, btn.data.side);
        } else if (btn.data.mode === 'jamb') {
          this.toggleJambSelection(plan, localPoint, btn.data.side);
        } else if (btn.data?.mode === 'cutaway') {
          this.toggleCutawaySelection(plan, localPoint);
        } else if (btn.data?.mode === 'splashback') {
          this.toggleSplashback(btn.data.plan, btn.data.side);
          this.onSplashbackChange.emit();
        }

        this.updatePreview();
      }
      return;
    }
    const obj = this.scene.children.find(c => c.userData['id'] === btn.targetId);
    if (!obj) return;

    const plan = obj.userData['plan'];
    if (!plan) return;

    // Update Rotation State (0-3)
    // 0: 0deg, 1: -90deg, 2: -180deg, 3: -270deg
    // Rotate Right (Clockwise) -> -90deg -> Increment state? 
    // State 0 -> Right -> State 1 (-90)

    if (btn.type === 'rotate_cutting_left' || btn.type === 'rotate_cutting_right') {
      const cuttingIdx = btn.data?.cuttingIdx as number;
      const cc = plan?.configurationCuttings?.[cuttingIdx];
      if (!cc) return;
      let cState = cc.rotationState || 0;
      cState = btn.type === 'rotate_cutting_right' ? (cState + 1) % 4 : (cState - 1 + 4) % 4;
      cc.rotationState = cState;
      this.updatePreview();
      return;
    }

    let currentState = plan.shapeRotationState || 0;

    if (btn.type === 'rotate_right') {
      currentState = (currentState + 1) % 4;
    } else {
      currentState = (currentState - 1 + 4) % 4;
    }

    plan.shapeRotationState = currentState;

    // Update Three.js rotation
    // Standard mathematical rotation is Counter-Clockwise (CCW).
    // If State 1 is "Right" (-90deg), then:
    // State 0: 0
    // State 1: -PI/2
    // State 2: -PI
    // State 3: -3PI/2 (or PI/2)

    obj.rotation.z = -currentState * (Math.PI / 2);

    // Force update matrix to ensure bounding boxes are correct for UI overlays immediately
    obj.updateMatrixWorld(true);
    this.validateAllPlans();

    // We might need to adjust position if rotating around center vs top-left?
    // Fabric usually rotates around center. Three.js rotation is around pivot (0,0,0).
    // If our shape (0,0) is top-left, it will rotate around top-left.
    // If we want center rotation, we should center the geometry or wrap in a pivot group.
    // For now, let's stick to top-left rotation if that's what the origin represents, 
    // OR re-center the geometry and move the group to center.
    // Given Fabric implementation often handles centers, we might want to offset.
    // Let's verify standard behavior first. If pivot is top-left, rotating -90 moves the shape up/left visually?
    // Let's assume for now simple rotation z is enough, visual validation will confirm.

    this.updateUIOverlays();
  }

  // --- Stub Methods for IWorkbenchPreview Compatibility ---
  // These methods are required by the interface but not all are implemented in Three.js

  deletePlan(): number {
    if (!this.scene) return 0;

    const plans = this.scene.children.filter(c => c.userData['plan']);
    if (plans.length === 0) return 0;

    // Find highest ID (last added)
    const maxId = Math.max(...plans.map(p => p.userData['id']));

    this.removePlan(maxId);
    this.zoomToFit();

    return maxId;
  }

  removePlan(id: number): void {
    const obj = this.scene.children.find(c => c.userData['id'] === id);
    if (obj) {
      this.scene.remove(obj);
      this.disposeObject(obj);

      if (this.selectedObject === obj) {
        this.selectedObject = null;
        this.highlightSelection(null);
      }

      this.updateUIOverlays();
    }
  }

  flipMushroomShape(plan: any): void {
    // The plan state is likely updated by the parent/service.
    // We just need to refresh the geometry.
    this.updatePreview();
  }

  addOrUpdateShape(): void {
    this.updatePreview();
  }

  private isPlanAtOrigin(plan: any): boolean {
    return !plan.origin || (plan.origin.x === 0 && plan.origin.y === 0);
  }

  updateLegend(showSplashback: boolean): void {
    // Legend not implemented in Three.js version
    console.log('Legend display:', showSplashback);
  }

  hideLegend(): void {
    // No-op for Three.js
  }

  configurationIsNotLoad(): boolean {
    return this.workbenchService.configuration?.workbenchConfigurationPlans === undefined;
  }

  loadExistingConfiguration(): void {
    if (this.workbenchService.configuration) {
      this.updatePreview();
    }
  }

  removePlanOrGroupDecor(planOrGroup: any): void {
    console.warn('removePlanOrGroupDecor not implemented in Three.js preview');
  }

  previewSelectPlanById(id: number): void {
    const obj = this.scene.children.find(c => c.userData['id'] === id);
    if (obj) {
      this.selectedObject = obj;
      this.highlightSelection(obj);
      this.onPlanSelect.emit(id);
      this.updateUIOverlays();
    }
  }

  private refreshGroupAssembly() {
    if (!this.scene) return;

    // Get all plans
    const allObjects = this.scene.children.filter(c => c.userData['plan']);
    if (allObjects.length === 0) return;

    // Use Service to find groups
    const groupsIds = this.collisionService.findGroups(allObjects);

    // Map IDs back to Objects
    const groups: THREE.Object3D[][] = groupsIds.map(ids =>
      ids.map(id => allObjects.find(o => o.userData['id'] === id)).filter(o => !!o)
    );

    // Process Groups
    // Process Groups
    groups.forEach((group, index) => {
      // Check if this group structure ALREADY exists
      // If all members have the SAME groupId, and that groupId is unique to them, we are good.

      const existingGroupIds = new Set<string>();
      let allMembersHaveGroup = true;
      group.forEach(m => {
        if (m.userData['groupId']) existingGroupIds.add(m.userData['groupId']);
        else allMembersHaveGroup = false;
      });

      // If we have a single existing group ID that covers everyone and no one else... 
      // Checking "no one else" is hard locally, but if existingGroupIds.size == 1 && allMembersHaveGroup
      // we can assume it's stable?
      // Actually, if we just detect geometry, we might want to force update if geometry changed.
      // But if geometry is same, we shouldn't emit.

      const isStable = group.length > 1 && allMembersHaveGroup && existingGroupIds.size === 1;

      if (isStable) return; // Do nothing, stable

      // If not stable, we rebuild
      const groupId = group.length > 1 ? `group_${index}_${Date.now()}` : null;

      if (group.length > 1) {
        const ids = group.map(m => m.userData['id']);
        this.onGroupCreate.emit(ids);
        group.forEach(member => member.userData['groupId'] = groupId);
      } else {
        // Single item, ensure it has NO group
        const member = group[0];
        if (member.userData['groupId']) {
          this.onGroupDelete.emit(member.userData['id']);
          delete member.userData['groupId'];
        }
      }
    });
  }

  public enable3DMode() {
    this.update2DIndicatorsVisibility(true);
    this.sceneService.enable3DMode(() => {
      this.updateUIOverlays();
    });
    // is3DMode now delegates to sceneService via getter — no manual sync needed
  }

  public disable3DMode() {
    this.sceneService.disable3DMode(() => {
      this.updateUIOverlays();
      this.update2DIndicatorsVisibility(false);
    });
    // is3DMode now delegates to sceneService via getter — no manual sync needed
  }

  private update2DIndicatorsVisibility(hide?: boolean) {
    const shouldHide = hide ?? this.is3DMode;
    this.scene.traverse((child) => {
      const t = child.userData?.['type'];
      if (t === 'jamb-indicator' || t === 'cutaway-indicator' || t === 'edge-indicator' || t === 'cutting-indicator') {
        child.visible = !shouldHide;
      }
    });
  }

  // Helper to get total scene bounding box
  private computeSceneBounds(): THREE.Box3 {
    const box = new THREE.Box3();
    let hasObjects = false;
    this.scene.children.forEach(c => {
      if (c.userData?.['plan']) {
        box.expandByObject(c);
        hasObjects = true;
      }
    });

    // If no objects, fallback to default small box around 0,0
    if (!hasObjects) {
      box.set(new THREE.Vector3(-100, -100, 0), new THREE.Vector3(100, 100, 0));
    }

    return box;
  }

  private toScreenPosition(objPos: THREE.Vector3): { x: number, y: number } {
    if (!this.container) return { x: 0, y: 0 };
    const vector = objPos.clone();
    vector.project(this.camera);
    const widthHalf = this.container.clientWidth / 2;
    const heightHalf = this.container.clientHeight / 2;
    return {
      x: (vector.x * widthHalf) + widthHalf,
      y: -(vector.y * heightHalf) + heightHalf
    };
  }
  
  public downloadSnapshot() {
    const node = document.getElementById('preview-section');
    if (node) {
      this.isSnapshotting = true;
      this.cdRef.detectChanges();
      setTimeout(() => {
        this.sceneService.downloadSnapshot(node);
        this.isSnapshotting = false;
        this.cdRef.detectChanges();
      }, 100);
    }
  }

  /**
   * Capture la vue 2D complète (canvas WebGL + overlays HTML) en base64 PNG.
   * Utilisé par configurators-2d.component.ts pour la génération du PDF.
   */
  public captureAsBase64(): Promise<string> {
    return new Promise<string>((resolve) => {
      const node = document.getElementById('preview-section');
      if (!node) {
        resolve('');
        return;
      }

      const savedMode = this.selectionStore.activeMode();

      // Block the effect-driven rAF overlay updates during capture to avoid races
      this.isCapturingSnapshot = true;
      this.isSnapshotting = true;
      this.selectionStore.setMode(InteractionModeEnum.SNAPSHOT);

      // First synchronous overlay update to populate all arrays (planIndicators, magnetismIcons, actionButtons)
      this.overlayUpdatePending = false;
      this._updateUIOverlays();
      this.cdRef.detectChanges();

      // Double-rAF: wait for the browser to paint the updated DOM before capturing
      requestAnimationFrame(() => {
        // Force Angular to flush DOM updates so html-to-image clones the final state
        this.cdRef.detectChanges();

        requestAnimationFrame(() => {
          htmlToImage.toPng(node, { backgroundColor: '#ffffff' })
            .then((dataUrl) => resolve(dataUrl))
            .catch((error) => {
              console.error('captureAsBase64 failed', error);
              resolve('');
            }).finally(() => {
              this.isCapturingSnapshot = false;
              this.isSnapshotting = false;
              this.selectionStore.setMode(savedMode);
              this.cdRef.detectChanges();
            });
        });
      });
    });
  }
}
